import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import BlogPost from '../components/BlogPost';
import blogData from '../data/blogs.json';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import FlowBackground from '../components/FlowBackground';

interface BlogPostData {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  date: string;
  author: string;
  image: string;
}

const BlogPage: React.FC = () => {
  const [posts, setPosts] = useState<BlogPostData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { slug } = useParams<{ slug?: string }>();

  useEffect(() => {
    try {
      setPosts(blogData.posts);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-grow flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-noordblue"></div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <Navbar />
        <main className="flex-grow flex items-center justify-center py-12">
          <div className="text-center max-w-2xl px-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Oeps, er is iets misgegaan</h2>
            <p className="text-lg text-gray-600 mb-6">
              Het lukt ons niet om de blogberichten te laden. Probeer het later opnieuw.
            </p>
            <Button asChild>
              <Link to="/" className="bg-noordblue hover:bg-noordblue-dark">
                Terug naar home
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (slug) {
    const post = posts.find((p) => p.slug === slug);
    if (!post) {
      return (
        <div className="min-h-screen flex flex-col">
          <Navbar />
          <main className="flex-grow flex items-center justify-center">
            <div className="text-center">
              <h1 className="text-4xl font-bold mb-4">Post niet gevonden</h1>
              <Link to="/blog" className="text-blue-600 hover:underline">
                Terug naar alle berichten
              </Link>
            </div>
          </main>
          <Footer />
        </div>
      );
    }
    
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-grow">
          <div className="container mx-auto px-4 py-12 max-w-4xl">
            <Link
              to="/blog"
              className="inline-flex items-center text-blue-600 hover:text-blue-800 mb-6"
            >
              <svg
                className="w-4 h-4 mr-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              Terug naar overzicht
            </Link>
            <BlogPost {...post} isPreview={false} />
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <FlowBackground />
      <Navbar />
      <main className="flex-grow">
        <div className="container mx-auto px-4 py-16 max-w-5xl">
          <div className="text-center mb-16">
            <h1 className="text-4xl font-bold text-noordblue mb-4">Onze Blog</h1>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Blijf op de hoogte van de laatste ontwikkelingen, inzichten en tips over AI voor jouw bedrijf
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {posts.map((post) => (
              <div key={post.id} className="group">
                <BlogPost {...post} />
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default BlogPage;
